import { FunctionComponent, useCallback, useContext } from 'react'
import styled, { css } from 'styled-components'
import { ellipsis } from 'polished'

import { tabletCSS } from '@measures/responsive'
import { Link } from '@cook-types/cook'
import useTracking from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import contextViewport, { ViewportTypeValues } from '@contexts/viewport'
import Blink from '@components/Blink'

interface ContentProps {
  order: number
  title: string
  description?: string
  link?: Link
  articleId: number
}

interface ClickableProps extends Link {
  ariaLabel?: string
  onClick: () => void
}

type StyledCardContentProps = Pick<ContentProps, 'order'>

const StyledCardContent = styled.div<StyledCardContentProps>`
  ${({
    theme: {
      colors: {
        white,
        grey: { grey_100 },
      },
    },
    order,
  }) => {
    return css`
      height: 200px;
      box-sizing: border-box;
      font-size: 16px;
      line-height: 20px;
      color: ${grey_100};
      background-color: ${white};
      border-radius: 6px;
      position: relative;
      padding: 20px;
      display: grid;
      grid-template-rows: 44px 80px 1fr;
      grid-gap: 8px;
      ${tabletCSS(css`
        ${order === 5 && 'margin-right: 16px;'}
      `)}
    `
  }}
`

const StyledOrder = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_10 },
      },
      fonts: { goodOT },
    },
  }) => {
    return css`
      position: absolute;
      top: 0;
      right: 20px;
      color: ${grey_10};
      font-family: ${goodOT};
      font-weight: 800;
      font-size: 120px;
      line-height: 120px;
      font-style: italic;
      z-index: 1;
    `
  }}
`

const Title = styled.div`
  ${({
    theme: {
      fonts: { goodOT },
    },
  }) => {
    return css`
      font-weight: 800;
      font-family: ${goodOT};
      font-size: 18px;
      line-height: 22px;
      z-index: 2;
      ${ellipsis('100%', 2)};
    `
  }}
`

const StyledDescription = styled.div`
  z-index: 2;
  ${ellipsis('100%', 4)};
`

const { ViewportTypeContext } = contextViewport

const StyledCTA = styled.span`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
      fonts: { goodOT },
    },
  }) => {
    return css`
      text-transform: uppercase;
      color: ${brand_55};
      font-family: ${goodOT};
      font-weight: 500;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-size: 14px;
      z-index: 2;
    `
  }}
`

const LinkWrapper = styled(Blink)`
  text-decoration: none;
  display: block;
  padding: 0;
  border: 0;
`

const DivWrapper = styled.div``

const CardContent: FunctionComponent<ContentProps> = ({
  title,
  description,
  order,
  link,
  articleId,
}) => {
  const viewportType = useContext(ViewportTypeContext)
  const isDesktop = viewportType === ViewportTypeValues.DESKTOP

  const trackingOnImpression = useCallback(() => {
    return {
      event: 'highfive_impression',
      eventCategory: 'highfive_widget',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: `impression_${order}`,
    }
  }, [order, articleId, title])

  const trackingOnClick = useCallback(() => {
    return {
      event: 'highfive_click',
      eventCategory: 'highfive_widget',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: `impressionClick_${order}`,
    }
  }, [order, articleId, title])

  const handleImpressionTrack = useTracking(trackingOnImpression)
  const handleClickTrack = useTracking(trackingOnClick)

  const hasLink = !!link?.href

  const clickableProps: ClickableProps | {} = hasLink
    ? {
        ...link,
        onClick: () => {
          handleClickTrack()
        },
        ...(link?.title ? { ariaLabel: link.title } : {}),
      }
    : {}

  const viewportRef = useViewportTracking({
    onImpression: handleImpressionTrack,
    track: (isDesktop && order === 1) || !isDesktop,
  })

  const cardContent = (
    <StyledCardContent order={order}>
      <StyledOrder>{order}</StyledOrder>
      <Title>{title}</Title>
      <StyledDescription>{description}</StyledDescription>
      {hasLink && <StyledCTA>Lire la suite</StyledCTA>}
    </StyledCardContent>
  )

  return hasLink ? (
    <LinkWrapper ref={viewportRef} {...(clickableProps as ClickableProps)}>
      {cardContent}
    </LinkWrapper>
  ) : (
    <DivWrapper ref={viewportRef}>{cardContent}</DivWrapper>
  )
}

export default CardContent
